import { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Card, CardContent } from '@saas-pages/shared/ui'
import { toolError, toolInvoke, toolSuccess } from '@saas-pages/shared/public/runtime'
import { numberLocale, useLocale, useT } from '@/shared/i18n'
import { ToolPageShell } from '@/shared/tool-shell/ToolPageShell'
import { CodeWorkspace } from '@/shared/ui/code-workspace'

const UTILITY = 'json-to-csv'
const AUTO_PREVIEW_BYTES = 128 * 1024

type DelimiterMode = 'comma' | 'semicolon' | 'tab' | 'pipe'
type DelimiterChar = ',' | ';' | '\t' | '|'
type JsonValue = null | boolean | number | string | JsonValue[] | { [key: string]: JsonValue }
type FlatRow = Record<string, string>
type ConvertResult = {
  ok: true
  csv: string
  rows: number
  columns: number
  flattenedFields: number
} | {
  ok: false
  error: 'empty' | 'invalid-json' | 'unsupported-root' | 'complexity-limit' | 'processing-failed'
  detail?: string
}
type FlattenResult =
  | { ok: true; flattenedFields: number; cells: number }
  | { ok: false; error: 'complexity-limit' }
type RowsResult =
  | { ok: true; rows: FlatRow[]; flattenedFields: number }
  | { ok: false; error: 'complexity-limit' }

const DELIMITER_OPTIONS: DelimiterMode[] = ['comma', 'semicolon', 'tab', 'pipe']
const DELIMITERS: Record<DelimiterMode, DelimiterChar> = {
  comma: ',',
  semicolon: ';',
  tab: '\t',
  pipe: '|',
}
const MAX_JSON_DEPTH = 128
const MAX_TABLE_ROWS = 10_000
const MAX_TABLE_COLUMNS = 2_000
const MAX_TABLE_CELLS = 20_000

function isRecord(value: JsonValue): value is Record<string, JsonValue> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function primitiveToCell(value: JsonValue): string {
  if (value === null) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return JSON.stringify(value)
}

function flattenValue(value: JsonValue, row: FlatRow): FlattenResult {
  const stack: Array<{ value: JsonValue; path: string; depth: number }> = [
    { value, path: '', depth: 0 },
  ]
  let flattenedFields = 0
  let cells = 0

  while (stack.length > 0) {
    const current = stack.pop()
    if (!current || current.depth > MAX_JSON_DEPTH) {
      return { ok: false, error: 'complexity-limit' }
    }

    if (isRecord(current.value)) {
      const entries = Object.entries(current.value)
      if (entries.length === 0 && current.path) {
        row[current.path] = '{}'
        cells += 1
      } else {
        for (let index = entries.length - 1; index >= 0; index -= 1) {
          const entry = entries[index]
          if (!entry) continue
          const [key, child] = entry
          stack.push({
            value: child,
            path: current.path ? `${current.path}.${key}` : key,
            depth: current.depth + 1,
          })
        }
      }
    } else if (Array.isArray(current.value)) {
      if (current.value.length === 0 && current.path) {
        row[current.path] = '[]'
        cells += 1
      } else {
        for (let index = current.value.length - 1; index >= 0; index -= 1) {
          const child = current.value[index]
          if (child === undefined) continue
          stack.push({
            value: child,
            path: current.path ? `${current.path}[${index}]` : `[${index}]`,
            depth: current.depth + 1,
          })
        }
      }
    } else {
      row[current.path || 'value'] = primitiveToCell(current.value)
      cells += 1
      if (current.path.includes('.') || current.path.includes('[')) {
        flattenedFields += 1
      }
    }

    if (cells > MAX_TABLE_CELLS || Object.keys(row).length > MAX_TABLE_COLUMNS) {
      return { ok: false, error: 'complexity-limit' }
    }
  }

  return { ok: true, flattenedFields, cells }
}

function rowsFromJson(value: JsonValue): RowsResult | null {
  if (Array.isArray(value)) {
    if (value.length === 0) return { ok: true, rows: [], flattenedFields: 0 }
    if (value.length > MAX_TABLE_ROWS) return { ok: false, error: 'complexity-limit' }

    let flattenedFields = 0
    let cells = 0
    const rows: FlatRow[] = []
    for (const item of value) {
      const row: FlatRow = {}
      const flattened = flattenValue(item, row)
      if (!flattened.ok) return flattened
      flattenedFields += flattened.flattenedFields
      cells += flattened.cells
      if (cells > MAX_TABLE_CELLS) return { ok: false, error: 'complexity-limit' }
      rows.push(row)
    }
    return { ok: true, rows, flattenedFields }
  }

  if (isRecord(value)) {
    const row: FlatRow = {}
    const flattened = flattenValue(value, row)
    if (!flattened.ok) return flattened
    return { ok: true, rows: [row], flattenedFields: flattened.flattenedFields }
  }

  return null
}

function collectHeaders(rows: FlatRow[]): string[] {
  const seen = new Set<string>()
  const headers: string[] = []

  rows.forEach((row) => {
    Object.keys(row).forEach((key) => {
      if (!seen.has(key)) {
        seen.add(key)
        headers.push(key)
      }
    })
  })

  return headers
}

function escapeCsvCell(value: string, delimiter: DelimiterChar): string {
  if (!value.includes(delimiter) && !/["\r\n]/.test(value)) return value
  return `"${value.replaceAll('"', '""')}"`
}

function buildCsv(rows: FlatRow[], delimiter: DelimiterChar): string {
  const headers = collectHeaders(rows)
  const outputRows = [
    headers.map((header) => escapeCsvCell(header, delimiter)).join(delimiter),
    ...rows.map((row) => (
      headers.map((header) => escapeCsvCell(row[header] ?? '', delimiter)).join(delimiter)
    )),
  ]

  return outputRows.join('\n')
}

function convertJson(input: string, delimiterMode: DelimiterMode): ConvertResult {
  if (!input.trim()) return { ok: false, error: 'empty' }

  let parsed: JsonValue
  try {
    parsed = JSON.parse(input) as JsonValue
  } catch (error) {
    return {
      ok: false,
      error: 'invalid-json',
      detail: error instanceof Error ? error.message : undefined,
    }
  }

  try {
    const result = rowsFromJson(parsed)
    if (!result) return { ok: false, error: 'unsupported-root' }
    if (!result.ok) return result

    const delimiter = DELIMITERS[delimiterMode]
    const headers = collectHeaders(result.rows)
    const exceedsMaterializedCellLimit = headers.length > 0
      && result.rows.length > Math.floor(MAX_TABLE_CELLS / headers.length)
    if (headers.length > MAX_TABLE_COLUMNS || exceedsMaterializedCellLimit) {
      return { ok: false, error: 'complexity-limit' }
    }
    return {
      ok: true,
      csv: buildCsv(result.rows, delimiter),
      rows: result.rows.length,
      columns: headers.length,
      flattenedFields: result.flattenedFields,
    }
  } catch {
    return { ok: false, error: 'processing-failed' }
  }
}

export function JsonToCsvPage() {
  const t = useT()
  const { locale } = useLocale()
  const localeCode = numberLocale(locale)
  const [input, setInput] = useState('')
  const [delimiterMode, setDelimiterMode] = useState<DelimiterMode>('comma')
  const [output, setOutput] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [resultStats, setResultStats] = useState<{
    rows: number
    columns: number
    flattenedFields: number
  } | null>(null)

  const inputStats = useMemo(() => {
    return {
      chars: input.length,
      lines: input ? input.split(/\r\n|\r|\n/).length : 0,
    }
  }, [input])
  const inputBytes = useMemo(() => new TextEncoder().encode(input).byteLength, [input])
  const requiresExplicitConvert = inputBytes > AUTO_PREVIEW_BYTES

  const setSource = useCallback((value: string) => {
    setInput(value)
    setOutput('')
    setError(null)
    setResultStats(null)
  }, [])

  const handleConvert = useCallback(() => {
    if (!input.trim()) {
      setOutput('')
      setError(null)
      setResultStats(null)
      return
    }

    const start = performance.now()
    toolInvoke(UTILITY, { delimiter: delimiterMode })

    const result = convertJson(input, delimiterMode)
    if (result.ok) {
      setOutput(result.csv)
      setError(null)
      setResultStats({
        rows: result.rows,
        columns: result.columns,
        flattenedFields: result.flattenedFields,
      })
      toolSuccess(UTILITY, Math.round(performance.now() - start), {
        delimiter: delimiterMode,
        rows: result.rows,
        columns: result.columns,
      })
      return
    }

    const message = result.error === 'invalid-json'
      ? t.jsonToCsv.errors.invalidJson.replace('{error}', result.detail ?? t.jsonToCsv.errors.unknown)
      : result.error === 'unsupported-root'
        ? t.jsonToCsv.errors.unsupportedRoot
        : result.error === 'complexity-limit'
          ? t.jsonToCsv.errors.complexityLimit
          : result.error === 'processing-failed'
            ? t.jsonToCsv.errors.processingFailed
            : t.jsonToCsv.errors.empty
    setOutput('')
    setResultStats(null)
    setError(message)
    const errorCode = result.error === 'complexity-limit'
      ? 'complexity-limit'
      : result.error === 'processing-failed'
        ? 'processing-failed'
        : result.error
    toolError(UTILITY, errorCode, { delimiter: delimiterMode })
  }, [delimiterMode, input, t.jsonToCsv.errors])

  useEffect(() => {
    if (requiresExplicitConvert) return
    const timeout = window.setTimeout(handleConvert, 250)
    return () => window.clearTimeout(timeout)
  }, [handleConvert, requiresExplicitConvert])

  const handleCopy = useCallback(async () => {
    if (!output) return
    try {
      await navigator.clipboard.writeText(output)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      setCopied(false)
    }
  }, [output])

  const handleDownload = useCallback(() => {
    if (!output) return
    const blob = new Blob([output], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'data.csv'
    link.click()
    URL.revokeObjectURL(url)
  }, [output])

  const handleClear = useCallback(() => {
    setSource('')
  }, [setSource])

  return (
    <ToolPageShell toolKey="jsonToCsv" width="wide">
      <CodeWorkspace
        error={error}
        controls={(
          <div className="grid gap-4 lg:grid-cols-[1fr_auto] lg:items-end">
            <div>
              <label
                htmlFor="json-csv-delimiter"
                className="mb-1.5 block text-xs font-medium text-muted-foreground"
              >
                {t.jsonToCsv.delimiter}
              </label>
              <select
                id="json-csv-delimiter"
                value={delimiterMode}
                onChange={(event) => {
                  setDelimiterMode(event.target.value as DelimiterMode)
                  setOutput('')
                  setError(null)
                  setResultStats(null)
                }}
                className="h-10 w-full rounded-md border bg-background px-3 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring lg:w-56"
              >
                {DELIMITER_OPTIONS.map((item) => (
                  <option key={item} value={item}>
                    {t.jsonToCsv.delimiters[item]}
                  </option>
                ))}
              </select>
            </div>

            {requiresExplicitConvert && (
              <Button data-code-run onClick={handleConvert} disabled={!input.trim()}>
                {t.jsonToCsv.convertLarge}
              </Button>
            )}
          </div>
        )}
        input={{
          title: t.jsonToCsv.input,
          value: input,
          onChange: setSource,
          placeholder: t.jsonToCsv.inputPlaceholder,
          ariaLabel: t.jsonToCsv.input,
          clear: { label: t.jsonToCsv.clear, onClick: handleClear },
          stats: [
            {
              label: t.jsonToCsv.characters,
              value: inputStats.chars.toLocaleString(localeCode),
            },
            {
              label: t.jsonToCsv.lines,
              value: inputStats.lines.toLocaleString(localeCode),
            },
          ],
        }}
        output={{
          title: t.jsonToCsv.output,
          value: output,
          readOnly: true,
          placeholder: t.jsonToCsv.outputPlaceholder,
          ariaLabel: t.jsonToCsv.output,
          actions: (
            <>
              <Button variant="outline" size="sm" onClick={handleCopy}>
                {copied ? t.jsonToCsv.copied : t.jsonToCsv.copy}
              </Button>
              <Button variant="ghost" size="sm" onClick={handleDownload}>
                {t.jsonToCsv.download}
              </Button>
            </>
          ),
          status: requiresExplicitConvert ? (
            <div className="border-t bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:bg-amber-950/30 dark:text-amber-100">
              {t.jsonToCsv.autoPreviewLimit}
            </div>
          ) : undefined,
        }}
      />

      {resultStats && (
        <section className="mt-6 grid gap-4 sm:grid-cols-3">
          <Card className="shadow-sm">
            <CardContent className="p-4">
              <div className="text-sm font-medium tracking-normal">{t.jsonToCsv.rows}</div>
              <div className="mt-1 text-2xl font-bold tabular-nums">
                {resultStats.rows.toLocaleString(localeCode)}
              </div>
            </CardContent>
          </Card>
          <Card className="shadow-sm">
            <CardContent className="p-4">
              <div className="text-sm font-medium tracking-normal">{t.jsonToCsv.columns}</div>
              <div className="mt-1 text-2xl font-bold tabular-nums">
                {resultStats.columns.toLocaleString(localeCode)}
              </div>
            </CardContent>
          </Card>
          <Card className="shadow-sm">
            <CardContent className="p-4">
              <div className="text-sm font-medium tracking-normal">{t.jsonToCsv.flattened}</div>
              <div className="mt-1 text-2xl font-bold tabular-nums">
                {resultStats.flattenedFields.toLocaleString(localeCode)}
              </div>
            </CardContent>
          </Card>
        </section>
      )}

      <section className="mt-10 space-y-5 border-t pt-8">
        <div>
          <h2 className="text-xl font-semibold tracking-normal">{t.jsonToCsv.howTitle}</h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            {t.jsonToCsv.how}
          </p>
        </div>
        <div>
          <h2 className="text-xl font-semibold tracking-normal">{t.jsonToCsv.whenTitle}</h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            {t.jsonToCsv.when}
          </p>
        </div>
      </section>
    </ToolPageShell>
  )
}
